'use client'

import { useParams } from 'next/navigation'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  use,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useSWR from 'swr'

import { useAuthContext } from '@/context/AuthContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { formatNumberWithKilo } from '@/lib/format'
import {
  checkUserVoucher,
  CoOwner,
  getInitialRoomData,
  Owner,
  postLikeRoom,
  postUnlikeRoom,
  Room,
  updateUserAge,
} from '@/services/cms'
import { DeviceTypeWithAndroidIOS } from '@/types/configs'

function getIsOwner(owner: Owner, coOwners: CoOwner[] | undefined, profileId: string) {
  const ownerIds = [owner, ...(coOwners || [])].map((item) => item.id)
  return ownerIds.includes(profileId || '')
}

type CheckEnterRoomContextType = {
  roomData: Room
  isOwner: boolean
  isLikeButtonEnabled: boolean
  setRoomData: Dispatch<SetStateAction<Room>>
  toggleLikeRoom: () => Promise<void>
  viewCountWithKilo: string
  likeCountWithKilo: string
  preferDevices: DeviceTypeWithAndroidIOS[]
  isPreferredDevice: boolean
  isTrailer: boolean
  setIsTrailer: Dispatch<SetStateAction<boolean>>
  needAgeVerification: boolean
  setNeedAgeVerification: Dispatch<SetStateAction<boolean>>
  passAgeVerification: () => Promise<void>
  isPremiumRoom: boolean
  isVoucherCheckPassed?: boolean
}

const CheckEnterRoomContext = createContext<CheckEnterRoomContextType | null>(null)

type CheckEnterRoomProviderProps = PropsWithChildren<{
  initialRoomData: Room
  coOwnersPromise: Promise<CoOwner[] | undefined> | null
  canPreview: boolean
}>

export function CheckEnterRoomProvider({
  children,
  initialRoomData,
  canPreview,
  coOwnersPromise,
}: CheckEnterRoomProviderProps) {
  const coOwners = coOwnersPromise ? use(coOwnersPromise) : undefined

  const { roomId } = useParams<{ roomId: string }>()
  const { store, sessionStore, setStore, setSessionStore } = useConfigsContext()
  const { isSignedIn, handleLogin } = useAuthContext()
  const { deviceTypeWithAndroidAndIOS } = useUAContext()

  const htcToken = store?.credentials?.htcToken
  const profileId = store?.profile?.id

  const [roomData, setRoomData] = useState(initialRoomData)
  const [isLikeButtonEnabled, setIsLikeButtonEnabled] = useState(!htcToken ? true : false)

  const viewCountWithKilo = formatNumberWithKilo(roomData.view_count)
  const likeCountWithKilo = formatNumberWithKilo(roomData.like_count)
  const preferDevices = roomData.preferred_devices || []
  const isPreferredDevice = preferDevices.includes(deviceTypeWithAndroidAndIOS)

  const isOwner = useMemo(
    () => getIsOwner(initialRoomData.owner, coOwners, store?.profile?.id || ''),
    [coOwners, initialRoomData.owner, store?.profile?.id],
  )
  const [isTrailer, setIsTrailer] = useState(false)
  const [needAgeVerification, setNeedAgeVerification] = useState(false)

  const isAdultOnly = !!roomData?.is_adult_only
  const passAgeVerification = async () => {
    setNeedAgeVerification(false)
    if (store?.profile?.id && store?.credentials?.htcToken) {
      try {
        await updateUserAge(store.profile.id, true, store.credentials.htcToken)
        setStore((prev) => ({
          ...prev,
          profile: prev && prev.profile ? { ...prev.profile, isOver18: true } : prev?.profile,
        }))
      } catch (error) {
        console.error(`Couldn't update age:${error}.`)
      }
    } else {
      setSessionStore((prev) => ({
        ...prev,
        isOver18: true,
      }))
    }
  }

  const isPremiumRoom = !!roomData?.is_voucher_room
  const shouldCheckVoucher = isPremiumRoom && !!profileId && !!roomData.hub_sid
  const { data: isVoucherPassedFromApi } = useSWR<boolean>(
    shouldCheckVoucher ? 'checkUserVoucher' : null,
    () => checkUserVoucher(roomData.hub_sid, profileId!),
  )

  const isVoucherCheckPassed =
    canPreview && isPremiumRoom
      ? true
      : shouldCheckVoucher
        ? isVoucherPassedFromApi
        : !isPremiumRoom

  useEffect(() => {
    if (isSignedIn) {
      setNeedAgeVerification(!store?.profile?.isOver18 && !isOwner && isAdultOnly)
    } else {
      setNeedAgeVerification(!sessionStore?.isOver18 && !isOwner && isAdultOnly)
    }
  }, [isAdultOnly, isOwner, store?.profile?.isOver18, sessionStore?.isOver18, isSignedIn])

  // 🥷🏼 Do again here for token with data
  useSWR(
    htcToken ? 'checkEnterRoomWithToken' : null,
    () => getInitialRoomData(roomId, htcToken, false),
    {
      onSuccess: (data) => {
        setRoomData(data!)
        setIsLikeButtonEnabled(true)
      },
    },
  )

  const toggleLikeRoom = async () => {
    if (!isSignedIn) {
      handleLogin()
      return
    }

    if (!roomData.id) return
    const isLiked = roomData.is_liked
    setIsLikeButtonEnabled(false)

    try {
      const result = isLiked
        ? await postUnlikeRoom(roomData.id, htcToken!)
        : await postLikeRoom(roomData.id, htcToken!)

      if (result?.status !== 200) {
        throw new Error('Failed to toggle like room status')
      }

      setRoomData((prev) => ({
        ...prev,
        is_liked: !isLiked,
        like_count: result.results.like_count,
      }))
    } catch (error) {
      console.error('toggle like room error:', error)
    } finally {
      setIsLikeButtonEnabled(true)
    }
  }

  return (
    <CheckEnterRoomContext.Provider
      value={{
        roomData,
        isOwner,
        isLikeButtonEnabled,
        setRoomData,
        toggleLikeRoom,
        viewCountWithKilo,
        likeCountWithKilo,
        preferDevices,
        isPreferredDevice,
        isTrailer,
        setIsTrailer,
        needAgeVerification,
        setNeedAgeVerification,
        passAgeVerification,
        isPremiumRoom,
        isVoucherCheckPassed,
      }}
    >
      {children}
    </CheckEnterRoomContext.Provider>
  )
}

export const useCheckEnterRoomContext = () => {
  const value = useContext(CheckEnterRoomContext)

  if (value == null) {
    throw new Error('useCheckEnterRoomContext cannot be used outside of CheckEnterRoomProvider')
  }

  return value
}
